import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Star, DollarSign, TrendingUp, FlaskConical, Repeat, Building2, Handshake, Rocket, Users, Brain } from 'lucide-react';
import { aiEvaluatorsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const ICON_MAP = {
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'flask-conical': FlaskConical,
  'repeat': Repeat,
  'building-2': Building2,
  'handshake': Handshake,
  'rocket': Rocket,
  'users': Users,
};

const ACCENT_COLORS = [
  { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700', badgeBg: 'bg-purple-100' },
  { bg: 'bg-teal-50', border: 'border-teal-200', accent: 'text-teal-700', badgeBg: 'bg-teal-100' },
  { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', badgeBg: 'bg-blue-100' },
  { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-700', badgeBg: 'bg-orange-100' },
];

export default function Team() {
  const { language } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiEvaluatorsAPI.getProfiles().then(data => {
      setProfiles(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const t = (es, en) => language === 'es' ? es : en;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#f3f0ff' }}>
              <Brain className="w-7 h-7" style={{ color: '#6949b6' }} />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="team-title">
            {t('Conoce a nuestro equipo de evaluadores expertos IA', 'Meet our AI expert evaluators team')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t(
              'Cada evaluador IA esta disenado con un perfil especializado que analiza las postulaciones desde una perspectiva unica, garantizando evaluaciones multidimensionales y objetivas.',
              'Each AI evaluator is designed with a specialized profile that analyzes applications from a unique perspective, ensuring multidimensional and objective evaluations.'
            )}
          </p>
        </div>
      </section>

      {/* Profile Cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-8">
          {profiles.map((profile, idx) => {
            const colors = ACCENT_COLORS[idx % ACCENT_COLORS.length];
            const titleText = profile.title?.[language] || profile.title?.es || '';
            const nicknameText = profile.nickname?.[language] || profile.nickname?.es || '';
            const quoteText = profile.quote?.[language] || profile.quote?.es || '';

            return (
              <Card key={profile.slug || idx} className={`overflow-hidden border-2 ${colors.border} transition-all hover:shadow-lg`} data-testid={`team-card-${profile.slug}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Left - Avatar */}
                    <div className={`md:w-64 flex-shrink-0 ${colors.bg} flex flex-col items-center justify-center p-8`}>
                      <img
                        src={profile.avatar}
                        alt={profile.name}
                        className="w-36 h-36 rounded-full object-cover border-4 border-white shadow-xl"
                      />
                      <p className="mt-4 text-xs font-bold uppercase tracking-widest text-gray-500 text-center">
                        &ldquo;{nicknameText}&rdquo;
                      </p>
                      {/* Stars */}
                      <div className="flex items-center gap-0.5 mt-2">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} className={`w-4 h-4 ${i <= Math.floor(profile.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">{profile.rating}</span>
                      </div>
                    </div>

                    {/* Right - Info */}
                    <div className="flex-1 p-8">
                      <h2 className="text-2xl font-bold text-gray-900">{profile.name}</h2>
                      <p className={`text-sm font-semibold uppercase tracking-wide ${colors.accent} mb-4`}>
                        {titleText}
                      </p>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-5">
                        {(profile.badges || []).map((badge, bi) => {
                          const IconComp = ICON_MAP[badge.icon] || Star;
                          const badgeText = badge.text?.[language] || badge.text?.es || '';
                          return (
                            <Badge key={bi} variant="secondary" className={`${colors.badgeBg} ${colors.accent} border-0 text-xs py-1.5 px-3`}>
                              <IconComp className="w-3.5 h-3.5 mr-1.5" /> {badgeText}
                            </Badge>
                          );
                        })}
                      </div>

                      {/* Quote */}
                      <blockquote className="text-gray-600 italic border-l-3 pl-4 text-base leading-relaxed" style={{ borderLeftWidth: '3px', borderLeftColor: '#d1d5db' }}>
                        &ldquo;{quoteText}&rdquo;
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
